import { createApp, h, defineAsyncComponent } from 'vue';
import { reactive } from 'vue';
import { raygunPlugin, rg4js } from './lib/raygun';
import router from './router';
import config from './config';
import { useAuthorization } from './lib/auth/useAuthorization';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { useUserStore } from "./store/user";
import { useAppStore } from "./store/app";

import AppWrapper from './AppWrapper.vue';
import PrimeVue from 'primevue/config';

import EventBus from '@/layouts/slots/event-bus';

//validation
import VuelidatePlugin from '@vuelidate/core';

// layouts (async — defers default layout from auth-only sessions)
const layoutLoaders = {
  empty: () => import('./layouts/Empty.vue'),
  default: () => import('./layouts/Default.vue'),
  auth: () => import('./layouts/Auth.vue'),
};
const LayoutEmpty = defineAsyncComponent(layoutLoaders.empty);
const LayoutDefault = defineAsyncComponent(layoutLoaders.default);
const LayoutAuth = defineAsyncComponent(layoutLoaders.auth);

// Start downloading what the first render needs while the auth chain runs.
// The layout and the route component are lazy chunks that would otherwise
// only be requested after app.mount() and after the router guard resolves,
// one after the other, on top of the auth round trips. webpack dedupes the
// request when the async components load the same modules later.
{
  const target = router.resolve(window.location.pathname + window.location.search + window.location.hash);
  layoutLoaders[target.meta?.layout || 'empty']?.().catch(() => {});
  for (const record of target.matched) {
    const loader = record.components?.default;
    if (typeof loader === 'function') loader().catch(() => {});
  }
}

// firebase
import { auth, handleAuthStateChange } from './lib/firebase';
import { initializeStatsig } from './lib/statsig';

// analytics
import VueGtag from 'vue-gtag-next';


import Button from 'primevue/button';
import Card from 'primevue/card';
import Checkbox from 'primevue/checkbox';
import Column from 'primevue/column';
import { SCConfirmService, Bootstrap } from '@scale-computing/scale-design-framework';
import DataTable from 'primevue/datatable';
import Dialog from 'primevue/dialog';
import Divider from 'primevue/divider';
import Dropdown from 'primevue/dropdown';
import InlineMessage from 'primevue/inlinemessage';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import Paginator from 'primevue/paginator';
import Panel from 'primevue/panel';
import ProgressBar from 'primevue/progressbar';
import Ripple from 'primevue/ripple';
import Sidebar from 'primevue/sidebar';
import Textarea from 'primevue/textarea';
import Toast from 'primevue/toast';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';

import 'primevue/resources/primevue.min.css';
import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import './App.scss';

// custom icons
import AppIcon from '@/components/icons/AppIcon';

// shared
import TimeStamp from '@/components/shared/TimeStamp';
import ZeroStateHeader from '@/components/shared/page/ZeroStateHeader';

// auth
import { waitForAuthReady } from './lib/auth/authReady';
import { startEarlyBootstrap } from './lib/auth/bootstrap';

import analytics from './lib/analytics';
import { useOrganizationStore } from './store/organization';

const app = createApp({
    render() {
        return h(AppWrapper);
    },
});

// pinia store (replaces vuex)
const pinia = createPinia();

// parse json safely when using localStorage
pinia.use(({ store }) => {
  store.$persistedstate = {
    storage: {
      getItem: (key) => {
        try {
          const storedValue = window.localStorage.getItem(key);
          return JSON.parse(storedValue);
        } catch (e) {
          console.error('Invalid JSON in localStorage, falling back to default state');
          return {};
        }
      },
      setItem: (key, value) => window.localStorage.setItem(key, JSON.stringify(value))
    }
  };
});

pinia.use(piniaPluginPersistedstate);
app.use(pinia);

const store =  {
  app: useAppStore(),
  user: useUserStore(),
  organization: useOrganizationStore()
};
app.config.globalProperties.$store = store;

// A returning visitor with a still valid token from the last visit gets the
// startup requests and the feature flags going now, instead of after
// Firebase has restored the session (see lib/auth/bootstrap.js). Should
// Firebase restore another account, initializeStatsig re-identifies first.
const early = startEarlyBootstrap(new URL(window.location.href).searchParams.get('org') || store.organization.orgId);
if (early) initializeStatsig({ userID: early.userId, email: early.email }).catch(() => {});

analytics.load();

app.config.globalProperties.$appState = reactive({ inputStyle: 'outlined', colorScheme: 'light', isNewThemeLoaded: false });

app.use(PrimeVue, { ripple: true });
app.use(SCConfirmService);
app.use(Bootstrap({
  "authorize": useAuthorization(app),
}));
app.use(ToastService);
app.use(router);
app.use(raygunPlugin);

// analytics
app.use(VueGtag, {
    property: {
        id: config.VUE_APP_GOOGLE_ANALYTICS_ID,
    },
    config: {
      id: config.VUE_APP_GOOGLE_ANALYTICS_ID,
      settings: {
        cookie_flags: 'SameSite=Lax;Secure',
      }
    },
    router,
});

// charts (lazy — only loaded when a chart component renders)
app.component('apexchart', defineAsyncComponent(
  () => import('vue3-apexcharts').then(m => m.default)
));

// custom icons
app.component('app-icon', AppIcon);

//shared
app.component('Timestamp', TimeStamp);
app.component('ZeroStateHeader', ZeroStateHeader);

// validation
app.use(VuelidatePlugin);


//layouts
app.component('layout-empty', LayoutEmpty);
app.component('layout-default', LayoutDefault);
app.component('layout-auth', LayoutAuth);

app.directive('tooltip', Tooltip);
app.directive('ripple', Ripple);

app.component('Button', Button);
app.component('Card', Card);
app.component('Checkbox', Checkbox);
app.component('Column', Column);
app.component('DataTable', DataTable);
app.component('Dialog', Dialog);
app.component('Divider', Divider);
app.component('Dropdown', Dropdown);
app.component('InlineMessage', InlineMessage);
app.component('InputText', InputText);
app.component('Message', Message);
app.component('Paginator', Paginator);
app.component('Panel', Panel);
app.component('ProgressBar', ProgressBar);
app.component('Sidebar', Sidebar);
app.component('Textarea', Textarea);
app.component('Toast', Toast);


let mounted = false;
auth.onAuthStateChanged(async(user) => {
    await handleAuthStateChange(user);
    await waitForAuthReady;
    // Initialize Statsig once per auth state — the call is idempotent so the
    // second auth event (e.g. token refresh) is a no-op; the watchers in
    // statsig.js push subsequent user/org changes via updateUserAsync.
    try { await initializeStatsig(); } catch (e) { rg4js('send', { error: e }); }
    if (!mounted) {
        app.mount('#app');
        mounted = true;
        if (config.VUE_APP_HOTJAR_ID) {
            import('vue-hotjar').then(({ default: HotjarPlugin }) => {
                app.use(HotjarPlugin, { id: config.VUE_APP_HOTJAR_ID });
            });
        }
    }
    EventBus.emit('app-init');
});

